import React, { useState, useEffect, useMemo, useCallback } from 'react';

import Axios from 'axios';

import styled from 'styled-components';

import { toISODate, getMoment } from '@libs/NwMoment';

import EventDrawer from '@components/EventDrawer/EventDrawer';
import EmptyState from '@components/Gui/EmptyState';
import { Loading } from '@components/Gui/Loading';
import NwDrawer from '@components/Gui/NwDrawer';

import AvailableEvent from './AvailableEvent';
import AvailableEventsHeader from './AvailableEventsHeader';

const Wrapper = styled.div`
    .search-content {
    }
`;

const defaultFilters = {
    includeCastings: true,
    includeJobs: false,
    includeExtJobs: false,
    nameFilter: '',
};

const AvailableEventsDrawer = ({ modelID, date, onClose, afterAddEvent }) => {
    const [isLoadingAvailableEvents, setIsLoadingAvailableEvents] = useState(false);
    const [allEvents, setAllEvents] = useState([]);
    const [filters, setFilters] = useState(defaultFilters);
    const [addModelsAs, setAddModelsAs] = useState(0);
    const [selectedEvent, setSelectedEvent] = useState(null);

    useEffect(() => {
        loadAvailableEvents();
    }, []);

    const loadAvailableEvents = async () => {
        setIsLoadingAvailableEvents(true);
        try {
            const postParams = {
                FDate: toISODate(getMoment(date).startOf('day')),
                TDate: toISODate(getMoment(date).endOf('day')),
                inclJobs: true,
                inclExtJobs: true,
                inclCastings: true,
                checkConflict: false,
                inclTravels: false,
                inclAccommodations: false,
                inclExternalJobs: false,
                inclFollowUps: false,
                inclMeetings: false,
                inclNotices: false,
                General: true,
                Warning: false,
            };
            const events = await Axios.post('/calendar/getevents', postParams);
            const available = events.Events.filter(
                (event) =>
                    !event.ModelsDates.some((modelDate) => modelDate.Models.some((model) => model.ModelID === modelID))
            );
            setAllEvents(available);
            setIsLoadingAvailableEvents(false);
        } catch (error) {
            setAllEvents([]);
            setIsLoadingAvailableEvents(false);
        }
    };

    const handleFilterChange = useCallback((key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    }, []);

    const getFilteredEvents = useCallback((events, { includeCastings, includeJobs, includeExtJobs, nameFilter }) => {
        let filtered = events;

        filtered = filtered.filter((event) => {
            const typeName = (event.TypeName || '').toLowerCase();
            if (typeName === 'casting' && !includeCastings) return false;
            if (typeName === 'job' && !includeJobs) return false;
            if (typeName === 'ext' && !includeExtJobs) return false;
            return true;
        });

        if (nameFilter && nameFilter.trim().length > 0) {
            const search = nameFilter.trim().toLowerCase();
            filtered = filtered.filter((event) => {
                const name = (event.Name || '').toLowerCase();
                const projectName = (event.Project?.Name || '').toLowerCase();
                return name.includes(search) || projectName.includes(search);
            });
        }

        return filtered;
    }, []);

    const filteredEvents = useMemo(
        () => getFilteredEvents(allEvents, filters),
        [allEvents, filters, getFilteredEvents]
    );

    const handleAddEvent = () => {
        loadAvailableEvents();
        afterAddEvent();
    };

    const handleEventClick = (event) => {
        setSelectedEvent(event);
    };

    const handleCloseEventDrawer = () => {
        setSelectedEvent(null);
    };

    return (
        <NwDrawer title="Available Events" open onClose={onClose} width="40vw" maxWidth="800px">
            <Wrapper>
                <AvailableEventsHeader
                    filteredCount={filteredEvents.length}
                    totalCount={allEvents.length}
                    loading={isLoadingAvailableEvents}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    addModelsAs={addModelsAs}
                    onAddModelsAsChange={setAddModelsAs}
                />
                <div className="available-events-content">
                    {isLoadingAvailableEvents ? (
                        <Loading textBlack paddingTop="10rem" />
                    ) : (
                        <div className="events-list-container">
                            {filteredEvents.length === 0 ? (
                                <EmptyState message="No available events for this date" />
                            ) : (
                                filteredEvents.map((ev, index) => (
                                    <AvailableEvent
                                        key={`${ev.TypeName}-${ev.ID}-${index}`}
                                        modelID={modelID}
                                        date={date}
                                        addModelsAs={addModelsAs}
                                        onAddEvent={handleAddEvent}
                                        event={ev}
                                        onEventClick={handleEventClick}
                                    />
                                ))
                            )}
                        </div>
                    )}
                </div>
            </Wrapper>
            {selectedEvent && <EventDrawer eventID={selectedEvent} onClose={handleCloseEventDrawer} />}
        </NwDrawer>
    );
};

export default AvailableEventsDrawer;
